import _ from 'lodash';
import { useMemo } from 'react';
import getFeature from '../utils/features';
import useAppSelector from '../app/redux/useAppSelector';
import { PlanTier } from '../graphql/generated/graphql';
import useAllUsers from '../user/useAllUsers';

const FREE_SEAT_LIMIT = 10;

const getSeatCount = (
  userCount: number,
  guestCount: number,
  guestsPerSeat: number,
) => {
  const remainingGuests = Math.max(guestCount - userCount * guestsPerSeat, 0);
  const guestsSeats = Math.ceil(remainingGuests / (guestsPerSeat + 1));
  return userCount + guestsSeats;
};

const isGuestLimitMaxed = (
  userCount: number,
  guestCount: number,
  guestsPerSeat: number,
  seatLimit?: number | null,
) => {
  if (_.isNil(seatLimit)) {
    return false;
  }

  const userSeatsAvailableForGuests = Math.min(userCount, seatLimit);

  // Maximum number of guests with current number of users and seat limit
  const maxGuests =
    userSeatsAvailableForGuests * guestsPerSeat +
    (seatLimit - userSeatsAvailableForGuests) * (guestsPerSeat + 1);

  const availableGuests = Math.max(maxGuests - guestCount, 0);
  return availableGuests === 0;
};

const useSeatLimits = ({ guestsPerSeat }: { guestsPerSeat?: number } = {}) => {
  const { settings, planTier } = useAppSelector(
    (state) => state.session.organization,
  );
  const allUsers = useAllUsers();

  return useMemo(() => {
    if (allUsers.length === 0) {
      return {
        isSeatLimitMaxed: false,
        isGuestLimitMaxed: false,
        isLoading: true,
        seatCount: 0,
      };
    }
    const {
      ADMIN: adminCount = 0,
      USER: standardUserCount = 0,
      GUEST: guestsCount = 0,
    } = _.countBy(allUsers, 'type');

    const seatLimit =
      settings?.seatLimit ??
      (planTier === PlanTier.Free ? FREE_SEAT_LIMIT : null);

    const userCount = adminCount + standardUserCount;

    const usedGuestsPerSeat = guestsPerSeat ?? getFeature('guests_per_seat');

    const seatCount = getSeatCount(userCount, guestsCount, usedGuestsPerSeat);

    return {
      isSeatLimitMaxed: !_.isNil(seatLimit) ? seatCount >= seatLimit : false,
      isGuestLimitMaxed: isGuestLimitMaxed(
        userCount,
        guestsCount,
        usedGuestsPerSeat,
        seatLimit,
      ),
      isLoading: false,
      seatCount,
    };
  }, [allUsers, guestsPerSeat, planTier, settings?.seatLimit]);
};

export default useSeatLimits;
